import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Footer } from '@/components/Footer';
import {
    QrCode, Stamp, Users, BarChart3, Gift, Shield,
    Star, ArrowRight, Check, Menu, X, Zap, Heart,
    Smartphone, ScanLine, TrendingUp, ChevronRight, Sparkles
} from 'lucide-react';

const features = [
    { icon: QrCode, title: "QR Code unique", desc: "Chaque client possède son QR code personnel pour accumuler des points en un scan." },
    { icon: Stamp, title: "Tampons & Points", desc: "Deux modes de fidélité flexibles adaptés à votre activité." },
    { icon: Users, title: "Partenariats", desc: "Créez un réseau de fidélité local avec d'autres commerçants." },
    { icon: BarChart3, title: "Analytics", desc: "Suivez vos performances en temps réel avec des statistiques détaillées." },
    { icon: Zap, title: "Marketing Boost", desc: "Offrez des points bonus pour les interactions sur les réseaux sociaux." },
    { icon: Shield, title: "Anti-fraude", desc: "Sécurité intégrée avec limitation de scans et historique non modifiable." },
];

const steps = [
    { num: "01", icon: Smartphone, title: "Créez votre programme", desc: "Configurez votre carte de fidélité en quelques clics : tampons, points ou cashback." },
    { num: "02", icon: ScanLine, title: "Scannez les clients", desc: "Vos clients présentent leur QR code, vous scannez — les points s'ajoutent instantanément." },
    { num: "03", icon: Gift, title: "Récompensez la fidélité", desc: "Vos clients fidèles débloquent des récompenses et reviennent encore plus souvent." },
];

const plans = [
    {
        name: "Starter", price: "29", desc: "Idéal pour débuter",
        features: ["1 carte de fidélité", "Tampons ou points", "Scan QR code", "Gestion des clients", "Partenariats commerçants", "Statistiques basiques"],
        highlighted: false,
    },
    {
        name: "Pro", price: "49", desc: "Pour les commerces actifs",
        features: ["Jusqu'à 3 cartes", "Tout le Starter inclus", "Promotions clients", "Notifications push", "Logs d'activité"],
        highlighted: true,
    },
    {
        name: "Premium", price: "79", desc: "La solution complète",
        features: ["Cartes illimitées", "Tout le Pro inclus", "Marketing Boost (réseaux sociaux)", "Parrainage clients", "Récompenses anniversaire"],
        highlighted: false,
    },
];

const stats = [
    { value: "+340%", label: "Fidélisation client" },
    { value: "2 min", label: "Mise en place" },
    { value: "10k+", label: "Scans par mois" },
];

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

export default function LandingPage() {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <div className="min-h-screen bg-[#fafbfc]">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100" data-testid="landing-navbar">
                <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden">
                            <img src="/logo-fr-small.png" alt="FidelRun" className="w-14 h-14 object-contain" />
                        </div>
                        <span className="font-heading text-xl font-extrabold text-emerald-950 tracking-tight">FidelRun</span>
                    </Link>

                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-slate-500 hover:text-emerald-600 transition-colors text-sm font-medium">Fonctionnalités</a>
                        <a href="#how-it-works" className="text-slate-500 hover:text-emerald-600 transition-colors text-sm font-medium">Comment ça marche</a>
                        <a href="#pricing" className="text-slate-500 hover:text-emerald-600 transition-colors text-sm font-medium">Tarifs</a>
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <Link to="/connexion">
                            <Button variant="ghost" className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50/50 font-medium" data-testid="login-nav-btn">Connexion</Button>
                        </Link>
                        <Link to="/inscription">
                            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20 active:scale-[0.97] transition-all font-semibold" data-testid="register-nav-btn">
                                Commencer <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </Link>
                    </div>

                    <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2" data-testid="mobile-menu-btn">
                        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>

                {menuOpen && (
                    <div className="md:hidden px-6 pb-5 space-y-3 bg-white border-t border-slate-100">
                        <a href="#features" className="block py-2 text-slate-600 text-sm font-medium" onClick={() => setMenuOpen(false)}>Fonctionnalités</a>
                        <a href="#how-it-works" className="block py-2 text-slate-600 text-sm font-medium" onClick={() => setMenuOpen(false)}>Comment ça marche</a>
                        <a href="#pricing" className="block py-2 text-slate-600 text-sm font-medium" onClick={() => setMenuOpen(false)}>Tarifs</a>
                        <div className="pt-2 space-y-2">
                            <Link to="/connexion"><Button variant="outline" className="w-full border-emerald-200 text-emerald-700" data-testid="mobile-login-btn">Connexion</Button></Link>
                            <Link to="/inscription"><Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="mobile-register-btn">Commencer</Button></Link>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero */}
            <section className="pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/50 mb-6 px-3 py-1.5 text-xs font-semibold gap-1.5" data-testid="hero-badge">
                                <Sparkles className="w-3.5 h-3.5" /> Nouveau : Notifications push & Promotions
                            </Badge>
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6 text-slate-900">
                                La fidélité digitale qui{' '}
                                <span className="relative">
                                    <span className="text-emerald-500">booste</span>
                                    <svg className="absolute -bottom-1 left-0 w-full h-2 text-emerald-300/40" viewBox="0 0 100 8" preserveAspectRatio="none"><path d="M0 7 Q25 0 50 5 Q75 2 100 6" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/></svg>
                                </span>{' '}
                                votre commerce
                            </h1>
                            <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-8 max-w-lg">
                                Créez, gérez et partagez des programmes de fidélité modernes. Simple pour vous, gratifiant pour vos clients.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 mb-10">
                                <Link to="/inscription">
                                    <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 active:scale-[0.97] transition-all gap-2 w-full sm:w-auto h-12 px-7 text-[15px] font-semibold" data-testid="hero-cta-btn">
                                Créer mon compte <ArrowRight className="w-4 h-4" />
                                    </Button>
                                </Link>
                                <a href="#how-it-works">
                                    <Button size="lg" variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 w-full sm:w-auto h-12 px-7 text-[15px] font-medium" data-testid="hero-demo-btn">
                                        Découvrir
                                    </Button>
                                </a>
                            </div>

                            {/* Trust indicators */}
                            <div className="flex items-center gap-5 text-sm text-slate-400">
                                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Mise en place rapide</span>
                                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Sans engagement</span>
                                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> RGPD</span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            className="hidden md:block"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-br from-emerald-200/30 to-purple-200/20 rounded-3xl blur-3xl -z-10 scale-110" />
                                {/* Loyalty Card Mockup */}
                                <div className="rounded-2xl shadow-2xl shadow-slate-900/15 overflow-hidden" style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9, #5b21b6)', aspectRatio: '1.6/1' }}>
                                    <div className="relative h-full p-6 flex flex-col justify-between">
                                        {/* Grid pattern overlay */}
                                        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

                                        <div className="relative z-10">
                                            <p className="text-purple-200 text-[10px] tracking-[0.2em] font-semibold mb-1">CARTE FIDELITE</p>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-white/15 backdrop-blur rounded-xl flex items-center justify-center">
                                                        <Gift className="w-5 h-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-white font-extrabold text-xl tracking-tight">MON COMMERCE</h3>
                                                        <p className="text-purple-200 text-xs">Boulangerie du Centre</p>
                                                    </div>
                                                </div>
                                                <span className="bg-white/15 backdrop-blur text-white text-[10px] font-bold px-2.5 py-1 rounded-lg">TAMPONS</span>
                                            </div>
                                        </div>

                                        <div className="relative z-10 flex items-end justify-between gap-4">
                                            <div className="bg-white rounded-xl p-2.5 shadow-lg">
                                                <div className="w-[72px] h-[72px] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0id2hpdGUiLz48cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0iIzViMjFiNiIvPjxyZWN0IHg9IjQwIiB5PSIxMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjNWIyMWI2Ii8+PHJlY3QgeD0iNjAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiM1YjIxYjYiLz48cmVjdCB4PSI3MCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0iIzViMjFiNiIvPjxyZWN0IHg9IjEwIiB5PSI0MCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjNWIyMWI2Ii8+PHJlY3QgeD0iMzAiIHk9IjQwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiM1YjIxYjYiLz48cmVjdCB4PSI1MCIgeT0iNTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzViMjFiNiIvPjxyZWN0IHg9IjcwIiB5PSI0MCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjNWIyMWI2Ii8+PHJlY3QgeD0iMTAiIHk9IjcwIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIGZpbGw9IiM1YjIxYjYiLz48cmVjdCB4PSI0MCIgeT0iNzAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzViMjFiNiIvPjxyZWN0IHg9IjYwIiB5PSI4MCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjNWIyMWI2Ii8+PC9zdmc+')] bg-contain" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex gap-1.5 mb-2.5 flex-wrap">
                                                    {[...Array(10)].map((_, i) => (
                                                        <div key={i} className={`w-6 h-6 rounded-full border-2 ${i < 4 ? 'bg-white/90 border-white/90' : 'bg-white/10 border-white/20'}`} />
                                                    ))}
                                                </div>
                                                <p className="text-white/90 text-sm font-bold">4/10 tampons</p>
                                                <p className="text-purple-200 text-xs flex items-center gap-1 mt-0.5"><Gift className="w-3 h-3" /> Un croissant offert</p>
                                            </div>
                                        </div>

                                        <div className="relative z-10 flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                                            <span className="text-white/50 font-mono text-[10px]">A8F2B9C1</span>
                                            <span className="text-white/40 text-[10px] font-medium flex items-center gap-1"><img src="/logo-fr-small.png" alt="" className="w-3 h-3 rounded-sm opacity-60" /> FidelRun</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Floating stat */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8, duration: 0.5 }}
                                    className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl shadow-slate-900/8 border border-slate-100"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
                                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="font-heading font-extrabold text-sm text-slate-900">+340%</p>
                                            <p className="text-xs text-slate-400">Fidélisation client</p>
                                        </div>
                                    </div>
                                </motion.div>
                                {/* Floating reward */}
                                <motion.div
                                    initial={{ opacity: 0, y: -20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1, duration: 0.5 }}
                                    className="absolute -top-3 -right-3 bg-white rounded-2xl p-3.5 shadow-xl shadow-slate-900/8 border border-slate-100"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-9 h-9 bg-amber-50 rounded-lg flex items-center justify-center">
                                            <Star className="w-4 h-4 text-amber-500" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-xs text-slate-900">10 tampons</p>
                                            <p className="text-[10px] text-slate-400">= 1 café offert</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats bar */}
            <section className="py-6 border-y border-slate-100 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16">
                        {stats.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15 }}
                                className="text-center"
                            >
                                <p className="font-heading text-2xl md:text-3xl font-extrabold text-emerald-600">{s.value}</p>
                                <p className="text-xs text-slate-400 mt-0.5 font-medium">{s.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how-it-works" className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/50 mb-4 text-xs font-semibold">Simple & rapide</Badge>
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3 text-slate-900">Comment ça marche ?</h2>
                        <p className="text-base text-slate-500 max-w-xl mx-auto">Trois étapes suffisent pour digitaliser votre programme de fidélité.</p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-8"
                        initial="hidden" whileInView="visible" viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        {steps.map((s, i) => (
                            <motion.div key={i} variants={fadeUp} className="relative">
                                <div className="bg-white rounded-2xl p-7 border border-slate-100 hover:border-emerald-200 transition-all hover:shadow-lg hover:-translate-y-1 h-full" data-testid={`step-card-${i}`}>
                                    <span className="text-5xl font-extrabold text-emerald-100 absolute top-4 right-6">{s.num}</span>
                                    <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-5">
                                        <s.icon className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <h3 className="font-heading text-lg font-bold mb-2 text-slate-900">{s.title}</h3>
                                    <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                                </div>
                                {i < 2 && <ChevronRight className="hidden md:block absolute top-1/2 -right-5 w-5 h-5 text-slate-200 -translate-y-1/2" />}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/50 mb-4 text-xs font-semibold">Fonctionnalités</Badge>
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3 text-slate-900">Tout ce dont vous avez besoin</h2>
                        <p className="text-base text-slate-500 max-w-2xl mx-auto">Une solution complète pour digitaliser votre programme de fidélité.</p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                        initial="hidden" whileInView="visible" viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        {features.map((f, i) => (
                            <motion.div key={i} variants={fadeUp}>
                                <Card className="bg-[#fafbfc] border-slate-100 rounded-2xl hover:border-emerald-200 transition-all hover:-translate-y-1 hover:shadow-lg h-full group" data-testid={`feature-card-${i}`}>
                                    <CardContent className="p-6">
                                        <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-colors">
                                            <f.icon className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <h3 className="font-heading text-base font-bold mb-1.5 text-slate-900">{f.title}</h3>
                                        <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200/50 mb-4 text-xs font-semibold">Tarifs</Badge>
                        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3 text-slate-900">Des tarifs simples et transparents</h2>
                        <p className="text-base text-slate-500">Contactez-nous pour activer le forfait adapté à votre activité.</p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto"
                        initial="hidden" whileInView="visible" viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        {plans.map((plan, i) => (
                            <motion.div key={i} variants={fadeUp}>
                                <Card className={`rounded-2xl h-full flex flex-col transition-all hover:-translate-y-1 ${plan.highlighted ? 'border-2 border-emerald-500 shadow-xl shadow-emerald-500/10 relative bg-white' : 'border-slate-100 hover:border-emerald-200 hover:shadow-lg bg-white'}`} data-testid={`pricing-card-${plan.name.toLowerCase()}`}>
                                    {plan.highlighted && (
                                        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                                            <Badge className="bg-emerald-500 text-white border-0 text-xs px-3 py-1 shadow-md shadow-emerald-500/20">Populaire</Badge>
                                        </div>
                                    )}
                                    <CardHeader className="p-6 pb-4">
                                        <p className="text-sm font-semibold text-emerald-600 mb-3">{plan.name}</p>
                                        <div className="flex items-baseline gap-1">
                                            <span className="font-heading text-4xl font-extrabold text-slate-900">{plan.price}€</span>
                                            <span className="text-slate-400 text-sm font-medium">/mois</span>
                                        </div>
                                        <p className="text-sm text-slate-400 mt-1">{plan.desc}</p>
                                    </CardHeader>
                                    <CardContent className="px-6 pb-6 flex-1 flex flex-col">
                                        <ul className="space-y-3 flex-1">
                                            {plan.features.map((f, j) => (
                                                <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                                                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                                                    {f}
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="pt-6">
                                            <a href="tel:0693463628">
                                                <Button className={`w-full active:scale-[0.97] transition-all h-11 font-semibold ${plan.highlighted ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20' : 'bg-slate-50 border border-slate-200 text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200'}`} data-testid={`pricing-cta-${plan.name.toLowerCase()}`}>
                                                    Nous contacter
                                                </Button>
                                            </a>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                    <motion.div
                        className="bg-emerald-950 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
                        initial="hidden" whileInView="visible" viewport={{ once: true }}
                        variants={fadeUp} transition={{ duration: 0.5 }}
                    >
                        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_50%)]" />
                        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_80%,rgba(16,185,129,0.1),transparent_50%)]" />
                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white mb-4">
                                Prêt à fidéliser vos clients ?
                            </h2>
                            <p className="text-emerald-300/80 text-base mb-8 max-w-xl mx-auto leading-relaxed">
                                Rejoignez des centaines de commerçants qui utilisent FidelRun pour booster leur activité.
                            </p>
                            <Link to="/inscription">
                                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/30 active:scale-[0.97] transition-all gap-2 h-12 px-8 text-[15px] font-semibold" data-testid="final-cta-btn">
                                    Créer mon compte <ArrowRight className="w-4 h-4" />
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
}
